import { useEffect, useRef, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';

// Components
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { SEO } from './components/SEO';
import { Noise } from './components/ui/Noise';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { InteractiveBackground } from './components/ui/InteractiveBackground';
import { IntroAnimation } from './components/IntroAnimation';

// Pages - Lazy Loaded for Performance
const Home = lazy(() => import('./pages/Home'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const SolutionsPage = lazy(() => import('./pages/SolutionsPage'));
const WorkPage = lazy(() => import('./pages/WorkPage'));
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const InsightsPage = lazy(() => import('./pages/InsightsPage'));
const CareersPage = lazy(() => import('./pages/CareersPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const LegalPage = lazy(() => import('./pages/LegalPage'));

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

// Page transition wrapper - Smoother & More Pleasing
const PageTransition = ({ children }: { children: React.ReactNode }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -15 }}
      transition={{ 
        duration: 0.6, 
        ease: [0.22, 1, 0.36, 1] 
      }}
    >
      {children}
    </motion.div>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const location = useLocation();
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  return null;
};

// Lenis smooth scroll wrapper
const SmoothScroll = ({ children }: { children: React.ReactNode }) => {
  const lenisRef = useRef<Lenis | null>(null);

  useEffect(() => {
    // Initialize Lenis
    lenisRef.current = new Lenis({
      duration: 1.4,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1.1,
      touchMultiplier: 1.5,
      infinite: false,
    });

    // Connect Lenis to GSAP ScrollTrigger
    lenisRef.current.on('scroll', ScrollTrigger.update);

    gsap.ticker.add((time) => {
      lenisRef.current?.raf(time * 1000);
    });

    gsap.ticker.lagSmoothing(0);

    return () => {
      lenisRef.current?.destroy();
      gsap.ticker.remove((time) => {
        lenisRef.current?.raf(time * 1000);
      });
    };
  }, []);

  return <>{children}</>;
};

// Main app content
const AppContent = () => {
  const location = useLocation();
  const [showIntro, setShowIntro] = useState(false);
  const [introFinished, setIntroFinished] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    try {
      const hasPlayedIntro = sessionStorage.getItem('evolve_xo_intro_played');
      setShowIntro(!hasPlayedIntro);
    } catch (e) {
      setShowIntro(false);
    }
    setIsInitialized(true);
  }, []);

  const handleIntroComplete = () => {
    try {
      sessionStorage.setItem('evolve_xo_intro_played', 'true');
    } catch (e) {
      // Ignore storage errors
    }
    setIntroFinished(true);
  };

  return (
    <div className="relative min-h-screen bg-[#0A0A0A]">
      {/* Intro Animation Loader */}
      <AnimatePresence mode="wait">
        {showIntro && !introFinished && (
          <IntroAnimation onComplete={handleIntroComplete} />
        )}
      </AnimatePresence>

      {/* Skip to Content Link for Accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-[#FBEAD7] focus:text-[#0A0A0A] focus:rounded-lg focus:font-bold"
      >
        Skip to content
      </a>

      {/* Global SEO */}
      <SEO />
      
      {/* Global Interactive Elements */}
      <InteractiveBackground />
      
      {/* Navigation */}
      <motion.div
        initial={{ opacity: 0, y: -40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1], delay: 2.2 }}
        className="fixed top-0 left-0 right-0 z-[100]"
      >
        <Navbar />
      </motion.div>

      {/* Main Content with Page Transitions - margin-bottom reveals footer */}
      <main 
        id="main-content"
        className="relative z-10 bg-[#0A0A0A]"
        style={{ marginBottom: 'var(--footer-height)' }}
      >
        <Suspense fallback={<div className="h-screen w-full bg-[#0A0A0A] flex items-center justify-center"><div className="w-12 h-12 border-2 border-[#FBEAD7]/10 border-t-[#FBEAD7] rounded-full animate-spin" /></div>}>
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              <Route 
                path="/" 
                element={
                  <PageTransition>
                    <Home />
                  </PageTransition>
                } 
              />
              <Route 
                path="/services" 
                element={
                  <PageTransition>
                    <ServicesPage />
                  </PageTransition>
                } 
              />
              <Route 
                path="/solutions" 
                element={
                  <PageTransition>
                    <SolutionsPage />
                  </PageTransition>
                } 
              />
              <Route 
                path="/work" 
                element={
                  <PageTransition>
                    <WorkPage />
                  </PageTransition>
                } 
              />
              <Route 
                path="/work/:id" 
                element={
                  <PageTransition>
                    <ProjectDetailPage />
                  </PageTransition>
                } 
              />
              <Route 
                path="/about" 
                element={
                  <PageTransition>
                    <AboutPage />
                  </PageTransition>
                } 
              />
              <Route 
                path="/contact" 
                element={
                  <PageTransition>
                    <ContactPage />
                  </PageTransition>
                } 
              />
              <Route 
                path="/insights" 
                element={
                  <PageTransition>
                    <InsightsPage />
                  </PageTransition>
                } 
              />
              <Route 
                path="/careers" 
                element={
                  <PageTransition>
                    <CareersPage />
                  </PageTransition>
                } 
              />
              <Route 
                path="/privacy" 
                element={
                  <PageTransition>
                    <PrivacyPage />
                  </PageTransition>
                } 
              />
              <Route 
                path="/legal" 
                element={
                  <PageTransition>
                    <LegalPage />
                  </PageTransition>
                } 
              />
            </Routes>
          </AnimatePresence>
        </Suspense>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

// Main App Component
function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ErrorBoundary>
        <SmoothScroll>
          <ScrollToTop />
          <AppContent />
        </SmoothScroll>
      </ErrorBoundary>
    </Router>
  );
}

export default App;
